import { Button, TextInput, Toast, ToggleInput } from '@demandlocal/components'
import { FormProvider, useForm } from 'react-hook-form'
import React, { useMemo } from 'react'
import { ReactComponent as SuccessIcon } from '../../images/icons/check_fill.svg'
import { AnimatePresence, motion } from 'framer-motion'
import { toast } from 'react-hot-toast'
import useGetServiceEmailAddress from '../../api/dealership/utils/useGetServiceEmailAddress'
import useCheckSpreadsheetAccess from '../../api/dealership/utils/useCheckSpreadsheetAccess'
import { usePostGoogleSheetExport } from '../../api/dealership/exports/usePostGoogleSheetExport'
import { camelCase, isEmpty, startCase } from 'lodash'
import dayjs from 'dayjs'

type FormValues = {
  recurring: boolean
  sheet_url: string
}

export default function GoogleSheetsExport({
  setExportGoogleSheet,
  apiEndpoint,
  filters,
  columns,
}: {
  setExportGoogleSheet: React.Dispatch<React.SetStateAction<boolean>>
  apiEndpoint: string
  filters?: Record<string, unknown>
  columns: Array<unknown>
}): JSX.Element {
  const { mutateAsync } = usePostGoogleSheetExport()
  const serviceEmail = useMemo(
    () => 'link-one-export@demandlocal-link.iam.gserviceaccount.com',
    [],
  )

  const formContext = useForm<FormValues>({
    defaultValues: {
      recurring: false,
    },
  })

  const {
    handleSubmit,
    watch,
    reset,
    formState: { isSubmitting, isSubmitSuccessful },
  } = formContext

  const sheetURL = watch('sheet_url')

  const { mutateAsync: checkAccess } = useCheckSpreadsheetAccess()

  const onSubmit = async (values): Promise<void> => {
    const googleAccessToast = toast.loading('Checking access...')

    await checkAccess(
      {
        spreadsheet_url: sheetURL,
        service_email: serviceEmail,
      },
      {
        onSuccess: () => {
          toast.success('Access granted!', { id: googleAccessToast })
        },
        onError: (err) => {
          toast.error('Access denied!', { id: googleAccessToast })
          reset()
          return
        },
      },
    )

    const toastId = toast.loading('Generating report...')
    await mutateAsync({
      ...values,
      apiEndpoint: apiEndpoint,
      filters,
      recurring: values.recurring,
      serviceEmail: serviceEmail,
      headers: columns.map((column) => ({
        // @ts-ignore
        accessorKey: column.accessorKey,
        header:
          // @ts-ignore
          typeof column.header === 'function'
            ? // @ts-ignore
              startCase(camelCase(column.accessorKey))
            : // @ts-ignore
              column.header,
      })),
    })
    toast.success('Report sent', { id: toastId })
  }

  const getNext30MinuteInterval = (): number => {
    const minutesPastHour = dayjs().minute()
    const minutesUntilNext30 = 30 - (minutesPastHour % 30)
    return minutesUntilNext30 === 0 ? 30 : minutesUntilNext30
  }

  return (
    <>
      <FormProvider {...formContext}>
        <AnimatePresence>
          <motion.form
            animate={'open'}
            initial={'collapsed'}
            exit={'collapsed'}
            transition={{ type: 'spring', mass: 1, damping: 50, stiffness: 500 }}
            variants={{
              open: {
                opacity: 1,
                height: 'auto',
              },
              collapsed: {
                opacity: 0,
                height: 0,
              },
            }}
            onSubmit={handleSubmit(onSubmit)}>
            <div
              className={
                'p-5 relative rounded-md border-2 bg-primary-persian-blue/5 border-primary-persian-blue/10 mb-8'
              }>
              {isSubmitSuccessful && (
                <div
                  className={
                    'h-96 w-full mx-auto flex flex-col items-center justify-center'
                  }>
                  <SuccessIcon />
                  <div className={'pt-5 font-bold text-[28px]'}>
                    Great! Your report is now being generated.
                  </div>
                  <div className={'pt-3 font-medium text-base'}>
                    Approximate time for data to be loaded: {getNext30MinuteInterval()}{' '}
                    minutes
                  </div>
                  <a
                    className="font-bold pt-3 text-primary-persian-blue text-sm"
                    target={'_blank'}
                    rel={'noreferrer'}
                    href={sheetURL}>
                    Go to Google sheet
                  </a>
                  <div className={'pt-4 flex flex-row gap-2'}>
                    <Button
                      variant={'tertiary'}
                      type={'button'}
                      onClick={(e) => {
                        e.preventDefault()
                        reset()
                        setExportGoogleSheet(false)
                      }}>
                      Close
                    </Button>
                  </div>
                </div>
              )}
              {isSubmitting && (
                <div className={'w-full h-full absolute top-0 left-0 bg-white/50 z-20'} />
              )}
              {!isSubmitSuccessful && (
                <div>
                  <div
                    className={
                      'flex flex-row justify-between items-center border-b-2 pb-5 border-primary-persian-blue/10'
                    }>
                    <h3 className={'font-bold text-primary-xiketic text-lg'}>
                      Export to Google sheet
                    </h3>
                    <button
                      className="font-bold cursor-pointer"
                      onClick={(e) => {
                        e.preventDefault()
                        setExportGoogleSheet(false)
                      }}>
                      X
                    </button>
                  </div>

                  <div className={'flex flex-col pt-5 gap-4'}>
                    {!isEmpty(filters) && (
                      <Toast
                        className={'p-3'}
                        variant={'basic'}
                        text={'Warning: Export will apply your set filters'}
                      />
                    )}
                    <div className={'flex flex-row justify-between items-center'}>
                      <div>
                        <ToggleInput
                          name={'recurring'}
                          label={'Set as daily recurring report'}
                        />
                      </div>
                    </div>
                    <div>
                      <TextInput
                        name={'name'}
                        placeholder={'Enter name...'}
                        label={'Enter a name for your export'}
                        tooltip="This will be the name of the sheet name"
                      />
                    </div>
                    <div className="flex flex-col gap-2">
                      <TextInput
                        name={'sheet_url'}
                        label={'Google sheet URL'}
                        rules={{ required: true }}
                        placeholder="https://docs.google.com/spreadsheets/d/1-abcdefghijklmnopqrstuvxyz/edit#gid=0"
                      />
                      <h3 className="text-sm">
                        Share your Google sheet with this email address:{' '}
                        <strong>{serviceEmail}</strong>
                      </h3>
                    </div>
                  </div>
                  <div className={'flex flex-row gap-3 pt-5 justify-between'}>
                    <div className="w-fit">
                      <Button
                        variant="secondary"
                        disabled={!sheetURL}
                        onClick={async (e) => {
                          e.preventDefault()
                          const toastId = toast.loading('Checking access...')
                          if (!serviceEmail) {
                            toast.error('Access denied!', { id: toastId })
                            return
                          }

                          await checkAccess(
                            {
                              spreadsheet_url: sheetURL,
                              service_email: serviceEmail,
                            },
                            {
                              onSuccess: () => {
                                toast.success('Access granted!', { id: toastId })
                              },
                              onError: () => {
                                toast.error('Access denied!', { id: toastId })
                              },
                            },
                          )
                        }}>
                        Verify access
                      </Button>
                    </div>
                    <div>
                      <Button
                        disabled={isSubmitting}
                        animate={false}
                        variant={isSubmitSuccessful ? 'success' : 'rainbow'}
                        type={'submit'}>
                        {isSubmitSuccessful
                          ? 'Report sent'
                          : isSubmitting
                          ? 'Generating report...'
                          : 'Generate report'}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </motion.form>
        </AnimatePresence>
      </FormProvider>
    </>
  )
}
